"use client";

import { useState } from "react";
import ProductCardList from "@/components/ProductCardList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import AllTabPager from "./AllTabPager";
import PartnerFilter from "./PartnerFilter";
import type {
  FilterTabProducts,
  ProductCardListByFilterTabsClientProps,
} from "./types";
import { paginateAllTab } from "./utils/paginateAllTab";

const tabValue = (index: number) => String(index);

const ProductCardListByFilterTabsClient = ({
  title,
  subtitle,
  location,
  tabs,
  selectedPartners = [],
}: ProductCardListByFilterTabsClientProps) => {
  const [page, setPage] = useState(0);
  const [selectedTab, setSelectedTab] = useState(tabValue(0));

  const visibleTabs = tabs.flatMap((tab, index) =>
    tab.products.length > 0 ? [{ tab, index }] : [],
  );

  if (tabs.length === 0) return null;

  const selectedIsVisible = visibleTabs.some(
    ({ index }) => tabValue(index) === selectedTab,
  );
  const activeTab =
    visibleTabs.length === 0
      ? ""
      : selectedIsVisible
        ? selectedTab
        : tabValue(visibleTabs[0].index);
  const partnerOptions = tabs[Number(activeTab)]?.partnerOptions ?? [];

  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    setPage(0);
  };

  return (
    <div className="flex flex-col gap-4 rounded-[1.25rem] bg-putty-100 p-8">
      <div>
        {title ? (
          <h2
            className={cn(
              "text-xl font-bold lg:text-2xl",
              subtitle ? "mb-1" : "mb-4",
            )}
          >
            {title}
          </h2>
        ) : null}
        {subtitle ? (
          <p className="mb-4 text-base font-normal">{subtitle}</p>
        ) : null}
      </div>

      {visibleTabs.length > 0 ? (
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <TabsList className="h-auto w-full min-w-0 flex-nowrap justify-start gap-2 overflow-x-auto scrollbar-hide rounded-none bg-transparent p-0 py-1 pl-2 md:w-auto md:flex-1">
              {visibleTabs.map(({ tab, index }) => (
                <TabsTrigger
                  key={tabValue(index)}
                  value={tabValue(index)}
                  className={cn(
                    "shrink-0 grow-0 whitespace-nowrap rounded-[2rem] bg-putty-200 px-3 py-1 text-sm font-bold text-primary no-underline",
                    "first:rounded-[2rem] last:rounded-[2rem]",
                    "hover:bg-primary hover:text-white",
                    "data-[state=active]:bg-primary-500 data-[state=active]:text-white",
                    "data-[state=active]:hover:bg-primary-500 data-[state=active]:hover:text-white",
                  )}
                >
                  {tab.title}
                </TabsTrigger>
              ))}
            </TabsList>
            {partnerOptions.length > 0 ? (
              <div className="shrink-0">
                <PartnerFilter
                  key={activeTab}
                  options={partnerOptions}
                  selected={selectedPartners}
                />
              </div>
            ) : null}
          </div>
          {visibleTabs.map(({ tab, index }) => (
            <FilterTabPanel
              key={tabValue(index)}
              tab={tab}
              index={index}
              location={location}
              page={page}
              isSelected={tabValue(index) === activeTab}
              onPageChange={setPage}
            />
          ))}
        </Tabs>
      ) : null}
    </div>
  );
};

type FilterTabPanelProps = {
  tab: FilterTabProducts;
  index: number;
  location?: string;
  page: number;
  isSelected: boolean;
  onPageChange: (page: number) => void;
};

const FilterTabPanel = ({
  tab,
  index,
  location,
  page,
  isSelected,
  onPageChange,
}: FilterTabPanelProps) => {
  const pagination = tab.paginated
    ? paginateAllTab(tab.products, isSelected ? page : 0)
    : { products: tab.products, page: 0, pageCount: 1 };

  return (
    <TabsContent
      forceMount
      hidden={!isSelected}
      value={tabValue(index)}
      className="mt-4 rounded-b-xl px-0 data-[state=inactive]:hidden"
    >
      <ProductCardList
        products={pagination.products}
        location={location}
        shouldScroll={false}
      />
      {tab.paginated ? (
        <AllTabPager
          page={pagination.page}
          pageCount={pagination.pageCount}
          onPageChange={onPageChange}
        />
      ) : null}
    </TabsContent>
  );
};

export default ProductCardListByFilterTabsClient;
